import '@vaadin/button';
import { css, html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { TmBaseComponent } from '../tm-base.component';
import { ButtonTheme, TmButtonType } from './tm-button.types';

export const tagName = 'tm-button';
@customElement(tagName)
export class TmButton extends TmBaseComponent {
    constructor() {
        super();
        this.addEventListener('click', event => {
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
            }
        });
    }

    @property({ type: String, reflect: true })
    public type: TmButtonType = TmButtonType.None;

    @property({ type: String, reflect: true })
    public importance?: 'primary' | 'secondary' | 'tertiary' = 'secondary';

    @property({ type: Boolean })
    public disabled = false;

    @property({ type: String, reflect: true })
    public size?: 'large' | 'normal' | 'small' = 'normal';

    @property({ type: Boolean })
    public error = false;

    @property({ type: Boolean })
    public success = false;

    @property({ type: Boolean })
    public contrast = false;

    @property({ type: Boolean })
    public isIcon = false;

    @property({ type: Boolean })
    public fullWidth = true;

    @property({ attribute: false })
    public icon?: TemplateResult<2>;

    @property({ type: String, attribute: 'name' })
    public name?: string;

    private get successOrError() {
        if (this.success && this.error) {
            throw new Error('Button can not be of type success and error');
        }
        if (this.success) {
            return 'success';
        }
        if (this.error) {
            return 'error';
        }
        return undefined;
    }

    private get buttonTheme() {
        const { importance, size, successOrError } = this;
        const theme: ButtonTheme = { importance, size, successOrError };
        if (this.isIcon) {
            return 'icon';
        }
        return Object.values(theme)
            .map(val => val)
            .join(' ');
    }

    protected render() {
        return html`
            <vaadin-button
                part="component"
                theme=${this.buttonTheme}
                ?disabled=${this.disabled}
                class=${this.fullWidth ? 'w-full' : ''}
            >
                <div class="flex flex-row items-center">
                    ${this.icon
                        ? html`<tm-icon
                              .icon=${this.icon}
                              color="var(--jh-color-gray-0)"
                              class="mr-2"
                          ></tm-icon>`
                        : nothing}
                    <slot></slot>
                </div>
            </vaadin-button>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: inline-block;
                    --vaadin-button-height: 2.5rem;
                    --lumo-primary-color: var(--brand-primary);
                    --lumo-primary-contrast-color: var(--jh-color-gray-0);
                    --lumo-error-color: var(--jh-color-red-600);
                    --lumo-success-color: var(--jh-color-green-600);
                    --lumo-font-size-m: 1rem;
                    --lumo-font-size-l: 1rem;
                    --vaadin-button-border-radius: 0.5rem;
                    --primary-disabled-background: var(--brand-primary-30-light);
                    --primary-disabled-color: var(--jh-color-gray-0);
                    --secondary-background: var(--jh-color-gray-0);
                    --secondary-color: var(--brand-primary);
                    --secondary-disabled-background: var(--tm-color-gray-light-30);
                    --secondary-disabled-color: var(--brand-primary-30-light);
                    --border-color: var(--jh-color-gray-300);
                    --success-disabled-background: var(--content-positive-muted);
                    --error-disabled-background: var(--content-negative-muted);
                }
                :host([theme='dark']) {
                    --primary-disabled-background: var(--brand-primary-30-dark);
                    --primary-disabled-color: var(--brand-200);
                    --secondary-background: var(--tm-color-gray-medium-30);
                    --secondary-color: var(--jh-color-gray-0);
                    --secondary-disabled-background: var(--tm-color-gray-medium-15);
                    --secondary-disabled-color: var(--tm-color-gray-light-30);
                    --border-color: var(--tm-color-gray-light-30);
                }
                :host([disabled]) {
                    pointer-events: none;
                    cursor: not-allowed;
                }
                :host vaadin-button[theme~='primary'][disabled] {
                    background-color: var(--primary-disabled-background);
                    color: var(--primary-disabled-color);
                }
                :host vaadin-button[theme~='success'][disabled] {
                    background-color: var(--success-disabled-background);
                    color: var(--primary-disabled-color);
                }
                :host vaadin-button[theme~='error'][disabled] {
                    background-color: var(--error-disabled-background);
                    color: var(--primary-disabled-color);
                }
                :host vaadin-button[theme~='secondary'] {
                    background-color: var(--secondary-background);
                    border: 1px solid var(--border-color);
                    color: var(--secondary-color);
                }
                :host vaadin-button[theme~='secondary'][theme~='error'] {
                    color: var(--content-negative);
                }
                :host vaadin-button[theme~='secondary'][disabled] {
                    background-color: var(--secondary-disabled-background);
                    border-color: transparent;
                    color: var(--secondary-disabled-color);
                }
                :host vaadin-button[theme~='tertiary'] {
                    color: var(--content-action);
                }
                :host vaadin-button[theme='icon'] {
                    background-color: transparent;
                }
                :host vaadin-button[theme~='small'] {
                    --vaadin-button-height: 2rem;
                    --lumo-font-size-m: 0.875rem;
                    padding-left: 0.75rem;
                    padding-right: 0.75rem;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmButton;
    }
}
